import * as React from 'react';
import Spacing from 'components/Layout/components/Spacing';
import { Card, Divider, Paper, Tab, Tabs, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import DateRangePickerForm from 'components/Input/components/DateRangePickerForm';
import { FetchBordereauOptions, fetchMonthlyBordereau } from 'services/reporting';
import moment from 'moment';
import { useSelector } from 'react-redux';

import {
    getAllProgramYears,
    getAllProgramYearsByProduct,
    getProductLineTypesByCode,
    getUserGroups,
} from 'application/redux/selectors';
import { ReportingTabProps } from 'types/reporting.type';
import { AUTH_GROUPS } from '../../../../../constants';
import { AmType } from 'types/types.type';
import { getRolesContain } from 'helpers/roles';
import ReportDownloadsPanel, { ReportDownloadOption } from 'components/Reporting/components/ReportDownloadsPanel';

const DownloadReports: React.FunctionComponent<ReportingTabProps> = (props) => {
    const { reportName } = props;
    const cpProgramYears = useSelector(getAllProgramYearsByProduct('COMMERCIAL_PROPERTY'));
    const allProgramYears = useSelector(getAllProgramYears);
    const productLineTypes: Record<string, AmType> = useSelector(getProductLineTypesByCode);
    const userGroups = useSelector(getUserGroups);

    const [clickAnchor, setClickAnchor] = React.useState({} as HTMLAnchorElement);

    const options: ReportDownloadOption[] = [
        {
            label: 'Ally',
            entity: 'ALLY',
            contentType: 'text/csv',
            filename: 'Ally_Bordereaux',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            label: 'Policies In Force',
            entity: 'POLICIES_IN_FORCE',
            contentType: 'text/csv',
            filename: 'PoliciesInForce',
            qualifiers: cpProgramYears
                .sort((t1, t2) => {
                    return moment(t1.effectiveDate, 'MM/DD/YYYY', true).isBefore(
                        moment(t2.effectiveDate, 'MM/DD/YYYY', true)
                    )
                        ? 1
                        : -1;
                })
                .map((t) => t.programName),
            qualifierLabel: 'Treaty',
            hideStartDate: true,
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Written_Earned',
            label: 'Written & Earned',
            entity: 'WRITTEN_AND_EARNED',
            contentType: 'text/csv',
            qualifiers: Object.values(productLineTypes).map((plt) => ({
                code: plt.code,
                displayValue: plt.displayValue,
            })),
            qualifierLabel: 'Product Line',
            defaultStart: moment().startOf('year'),
            defaultEnd: moment(),
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Written_Earned_by_treaty',
            label: 'Written & Earned by Treaty',
            entity: 'WRITTEN_AND_EARNED_BY_TREATY',
            contentType: 'text/csv',
            qualifiers: allProgramYears
                .sort((t1, t2) => {
                    return moment(t1.effectiveDate, 'MM/DD/YYYY', true).isBefore(
                        moment(t2.effectiveDate, 'MM/DD/YYYY', true)
                    )
                        ? 1
                        : -1;
                })
                .map((t) => t.programName),
            qualifierLabel: 'Treaty',
            defaultStart: moment().startOf('year'),
            defaultEnd: moment(),
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Period_Earned',
            label: 'Period Earned',
            entity: 'PERIOD_EARNED',
            contentType: 'text/csv',
            qualifiers: Object.values(productLineTypes).map((plt) => ({
                code: plt.code,
                displayValue: plt.displayValue,
            })),
            qualifierLabel: 'Product Line',
            defaultStart: moment().startOf('year'),
            defaultEnd: moment(),
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            label: 'Lumen5 Stub PIF',
            entity: 'LUM5_STUB',
            contentType: 'text/csv',
            filename: 'Lum5Stub',
            hideStartDate: true,
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            label: 'Accelerant',
            entity: 'Accelerant',
            contentType: 'text/csv',
            filename: 'Accelerant_Bordereaux',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            label: 'Accelerant Exposure',
            entity: 'Accelerant_Exposure',
            contentType: 'text/csv',
            filename: 'LUM01 Exposure HAB GL',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Modeling_Data',
            label: 'Ally Modeling Data',
            hideStartDate: true,
            entity: 'MODELING_DATA',
            contentType: 'text/csv',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Modeling_Data',
            label: 'Ally Modeling Data (cond)',
            hideStartDate: true,
            entity: 'MODELING_DATA',
            contentType: 'text/csv',
            removeEmptyColumns: true,
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Transverse_Modeling_Data',
            label: 'Transverse Modeling Data',
            hideStartDate: true,
            entity: 'TRANSVERSE_MODELING_DATA',
            contentType: 'text/csv',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'CurrentBook',
            label: 'Current Book',
            hideStartDate: true,
            entity: 'CURRENT_BOOK',
            contentType: 'text/csv',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            label: 'Ally Taxable',
            entity: 'ALLY_STATE_TAXABLE',
            contentType: 'text/csv',
            filename: 'Ally_Yearly_Taxable',
            hideEndDate: true,
            qualifiers: ['FL', 'GA', 'LA', 'MI', 'MS', 'NJ', 'NY', 'NC', 'TN'],
            qualifierLabel: 'State',
            addQualifierToFilename: true,
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            filename: 'Underwriter_Activity',
            label: 'Underwriter Activity',
            hideStartDate: true,
            hideEndDate: true,
            entity: 'UNDERWRITER_ACTIVITY',
            contentType: 'text/csv',
            qualifiers: Object.values(productLineTypes).map((plt) => ({
                code: plt.code,
                displayValue: plt.displayValue,
            })),
            qualifierLabel: 'Product Line',
            permissions: [AUTH_GROUPS.RESTRICTED],
        },
        {
            label: 'Transverse',
            entity: 'Transverse',
            contentType: 'text/csv',
            filename: 'Transverse Bordereaux',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
        },
        {
            label: 'Ally Reinsurance',
            entity: 'ALLY_REINSURANCE',
            contentType: 'text/csv',
            filename: '50x10 Working File',
            permissions: [AUTH_GROUPS.ADMIN, AUTH_GROUPS.UNDERWRITER, AUTH_GROUPS.ACCOUNT_MANAGER],
            defaultStart: (() => {
                const now = moment();
                const anchor = now.clone().month(5).startOf('month'); // June 1 (start of fiscal year)
                if (now.isBefore(anchor)) anchor.subtract(1, 'year'); // shift to previous FY if before June 1
                const prevQuarterIndex = Math.floor(now.diff(anchor, 'months') / 3) - 1;
                return anchor.clone().add(prevQuarterIndex * 3, 'months'); // start of previous quarter
            })(),
            defaultEnd: (() => {
                const now = moment();
                const anchor = now.clone().month(5).startOf('month'); // June 1 (FY anchor)
                if (now.isBefore(anchor)) anchor.subtract(1, 'year');
                const prevQuarterIndex = Math.floor(now.diff(anchor, 'months') / 3) - 1;
                return anchor
                    .clone()
                    .add(prevQuarterIndex * 3 + 2, 'months')
                    .endOf('month');
            })(),
        },
    ];

    return (
        <ReportDownloadsPanel
            title="Download Reports"
            options={options.filter((option) => getRolesContain(userGroups, option.permissions))}
            fetchDownloadFile={(fetchOptions: FetchBordereauOptions) => fetchMonthlyBordereau(fetchOptions)}
        />
    );
};

export default DownloadReports;
